import { Link } from '@tanstack/react-router'

import {
  LedgerBadge,
  LedgerTable,
  type LedgerBadgeTone,
  type LedgerColumn,
} from '@/components/ui/LedgerTable'
import type { CustomerReminderState } from '@/lib/schemas/db'

export interface ReminderJobRow {
  id: string
  publicId?: string
  deviceType?: string
  /** True for "ready for collection — repair unsuccessful". */
  unsuccessful: boolean
}

export interface ReminderRow {
  id: string
  customerId: string
  customerName: string
  customerPublicId?: string
  /** Where the messages go, in WhatsApp digit form; empty when unknown. */
  phone: string
  /** Jobs currently ready for collection, read live from the customer's jobs. */
  jobs: ReminderJobRow[]
  state: CustomerReminderState
  /** Plain-language reason for a deferral/stop, or '' when on schedule. */
  noteLabel: string
  /** Display-ready (SA time) timestamps; empty when not applicable. */
  lastQueuedLabel: string
  /** When the reminder last queued a message, whatever became of it (for ordering). */
  lastQueuedAt?: string
  /** What became of that message: Sent, Queued, Held, Cancelled or Failed. */
  lastQueuedStatus?: string
  nextSendLabel: string
  /** What this reminder's messages did, counted from the WhatsApp log. */
  messageCounts: {
    sent: number
    held: number
    queued: number
    /** Pulled before sending (Stop, or the reminder was archived). */
    cancelled?: number
    failed?: number
  }
  /** Display-ready completion time for an archived reminder; empty otherwise. */
  archivedLabel: string
  /** Timestamp used for default ordering. */
  sortTimestamp: string
}

export type RemindersScope = 'active' | 'archived'

export interface RemindersScreenProps {
  rows: ReminderRow[]
  onStop?: (id: string) => void
  onResume?: (id: string) => void
  /** Which list this is; the page's Scheduled / Archived tabs choose it by URL. */
  scope?: RemindersScope
  /** Id of the reminder whose stop/resume is in flight. */
  busyId?: string | null
  errorMessage?: string | null
}

const stateTone: Record<CustomerReminderState, LedgerBadgeTone> = {
  active: 'teal',
  stopped: 'amber',
  expired: 'slate',
  archived: 'slate',
}

const stateLabel: Record<CustomerReminderState, string> = {
  active: 'Active',
  stopped: 'Stopped',
  expired: 'Expired',
  archived: 'Archived',
}

function buildColumns(opts: {
  onStop?: (id: string) => void
  onResume?: (id: string) => void
  busyId?: string | null
}): LedgerColumn<ReminderRow>[] {
  return [
    {
      key: 'customer',
      label: 'Customer',
      widthClass: 'w-44',
      sortValue: (row) => row.customerName.toLowerCase(),
      filter: 'text',
      filterValue: (row) => `${row.customerName} ${row.phone}`,
      render: (row) => (
        <div className="min-w-0">
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            <Link
              to="/customers/$id"
              params={{ id: row.customerPublicId ?? row.customerId }}
              className="font-medium text-blue-700 hover:underline"
            >
              {row.customerName}
            </Link>
          </div>
          <span className="font-mono text-[12px] text-slate-400">
            {row.phone || 'No phone number'}
          </span>
        </div>
      ),
    },
    {
      key: 'jobs',
      label: 'Waiting for collection',
            filter: 'text',
      filterValue: (row) =>
        row.jobs.map((job) => `${job.publicId ?? ''} ${job.deviceType ?? ''}`).join(' '),
      render: (row) =>
        row.state === 'archived' ? (
          <span className="text-slate-400">All collected {row.archivedLabel}</span>
        ) : (
        <ul className="space-y-0.5">
          {row.jobs.map((job) => (
            <li key={job.id} className="flex items-baseline gap-1.5">
              <Link
                to="/jobs/$id"
                params={{ id: job.publicId ?? job.id }}
                className="font-mono text-[12.5px] text-blue-700 hover:underline"
              >
                {job.publicId ?? 'Open'}
              </Link>
              <span className="text-slate-600">{job.deviceType || 'Device'}</span>
              {job.unsuccessful ? (
                <span className="text-[11.5px] text-slate-400">(unsuccessful)</span>
              ) : null}
            </li>
          ))}
        </ul>
        ),
    },
    {
      key: 'state',
      label: 'Status',
      widthClass: 'w-36',
      sortValue: (row) => row.state,
      filter: 'select',
      filterValue: (row) => row.state,
      filterOptions: (['active', 'stopped', 'expired', 'archived'] as const).map((state) => ({
        value: state,
        label: stateLabel[state],
      })),
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          <LedgerBadge tone={stateTone[row.state]}>{stateLabel[row.state]}</LedgerBadge>
          {row.noteLabel ? (
            <span className="text-[12px] text-slate-500">{row.noteLabel}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'messages',
      label: 'Messages',
      widthClass: 'w-36',
      sortValue: (row) => row.messageCounts.sent,
      cellClass: 'text-[12.5px] text-slate-600',
      render: (row) => {
        const { sent, held, queued, cancelled = 0, failed = 0 } = row.messageCounts
        const parts = [
          sent > 0 ? `${sent} sent` : '',
          queued > 0 ? `${queued} queued` : '',
          held > 0 ? `${held} held` : '',
          cancelled > 0 ? `${cancelled} cancelled` : '',
          failed > 0 ? `${failed} failed` : '',
        ].filter(Boolean)
        return (
          <div>
            {parts.length > 0 ? (
              <Link
                to="/reminders/$reminderId"
                params={{ reminderId: row.id }}
                className="text-blue-700 hover:underline"
              >
                {parts.join(' · ')}
              </Link>
            ) : (
              <span>No messages yet</span>
            )}
          </div>
        )
      },
    },
    {
      key: 'lastQueued',
      label: 'Last queued',
      widthClass: 'w-32',
      // No message queued yet sorts as the oldest ('' < any timestamp).
      sortValue: (row) => row.lastQueuedAt ?? '',
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-[12.5px] text-slate-600',
      render: (row) =>
        row.lastQueuedLabel ? (
          <div>
            <div>{row.lastQueuedLabel}</div>
            {row.lastQueuedStatus ? (
              <div className="text-[11.5px] text-slate-400">{row.lastQueuedStatus.toLowerCase()}</div>
            ) : null}
          </div>
        ) : (
          <span className="text-slate-400">Never</span>
        ),
    },
    {
      key: 'next',
      label: 'Next message',
      widthClass: 'w-32',
      sortValue: (row) => row.sortTimestamp,
      defaultSortDir: 'asc',
      cellClass: 'whitespace-nowrap text-[12.5px] text-slate-600',
      render: (row) => row.nextSendLabel || '—',
    },
    {
      key: 'actions',
      label: '',
      widthClass: 'w-20',
      render: (row) => {
        const busy = opts.busyId === row.id
        if (row.state === 'archived') return null
        if (row.state === 'stopped') {
          return (
            <button
              type="button"
              disabled={busy}
              className="rounded border border-slate-300 px-2 py-0.5 text-[12px] text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              onClick={() => opts.onResume?.(row.id)}
            >
              Resume
            </button>
          )
        }
        return (
          <button
            type="button"
            disabled={busy}
            title="Stop all WhatsApp messages to this customer"
            className="rounded border border-rose-300 px-2 py-0.5 text-[12px] text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            onClick={() => opts.onStop?.(row.id)}
          >
            Stop
          </button>
        )
      },
    },
  ]
}

export function RemindersScreen({
  rows,
  onStop,
  onResume,
  scope = 'active',
  busyId,
  errorMessage,
}: RemindersScreenProps) {
  const columns = buildColumns({ onStop, onResume, busyId })

  return (
    <div className="space-y-3">
      {errorMessage ? (
        <p role="alert" className="text-[13px] text-rose-600">
          {errorMessage}
        </p>
      ) : null}
      <LedgerTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        itemLabelPlural="reminders"
        emptyLabel={
          scope === 'archived'
            ? 'No reminders have been archived yet.'
            : 'No customers are waiting to collect anything.'
        }
        title={scope === 'archived' ? 'Archived reminders' : 'Scheduled reminders'}
        description={
          scope === 'archived'
            ? 'Reminders that ended because everything was collected, with the messages they sent.'
            : 'Customers with devices ready for collection, and when they will next hear from us. Stop a reminder to block every WhatsApp message to that customer.'
        }
        defaultSort={{ key: 'lastQueued', dir: 'desc' }}
        initialPageSize={25}
        minTableWidthClass="min-w-[1100px]"
      />
    </div>
  )
}
